import React, { useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import { ethers } from 'ethers'
import { useTranslation } from 'react-i18next'
import type { MarketplaceCollectionRef, MarketplaceSale } from '@mprolab/sdk'
import TypeBadge from './TypeBadge'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  text-align: left;
`

const Tag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.backgroundContrast};
  color: ${({ theme }) => theme.colors.cyan};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const PriceRow = styled(Flex)`
  align-items: baseline;
  justify-content: space-between;
  margin-top: 4px;
`

const TxLink = styled.a`
  color: ${({ theme }) => theme.colors.gray6};
  font-size: 11px;
  text-decoration: underline;
  &:hover {
    color: ${({ theme }) => theme.colors.cyan};
  }
`

const short = (address: string) => `${address.slice(0, 6)}…${address.slice(-4)}`

interface SaleCardProps {
  sale: MarketplaceSale
  collections: MarketplaceCollectionRef[]
  /** A person's public name where they set one; the short address otherwise. */
  displayName?: (wallet: string) => string
}

const SaleCard: React.FC<SaleCardProps> = ({ sale, collections, displayName }) => {
  const show = displayName ?? short
  const { t } = useTranslation()
  const { colors } = useTheme()

  const label = useMemo(
    () => collections.find((c) => c.nft === sale.nft)?.label ?? short(sale.nft),
    [collections, sale.nft]
  )
  const priceMpro = useMemo(() => {
    try {
      return ethers.utils.formatEther(sale.priceWei)
    } catch {
      return '?'
    }
  }, [sale.priceWei])

  return (
    <Card>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center" gap="6px">
          <Tag>{label}</Tag>
          {/*
            Which way the trade went. Two sales of the same licence type read
            identically otherwise, and the pair is the whole point: one buyer
            took a listing, one holder filled a standing offer.
          */}
          <TypeBadge wanted={sale.source === 'offer'} />
        </Flex>
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
          #{sale.tokenId}
        </Text>
      </Flex>
      <Text variant={TextVariant.BODY_DEFAULT_BOLD} isTrimmed linesToDisplay={1}>
        {label} #{sale.tokenId}
      </Text>
      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('marketplace.seller')}: {show(sale.seller)}
      </Text>
      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('marketplace.buyer')}: {show(sale.buyer)}
      </Text>
      <PriceRow>
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
          {t('marketplace.soldFor')}
        </Text>
        <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.cyan}>
          {priceMpro} MPRO
        </Text>
      </PriceRow>
      <TxLink href={`https://basescan.org/tx/${sale.txHash}`} target="_blank" rel="noreferrer">
        {t('marketplace.viewTx')}
      </TxLink>
    </Card>
  )
}

export default SaleCard
